import { Link, useNavigate } from "react-router-dom";
import { Store, ShieldCheck, ShoppingBag, ArrowRight, Package, BarChart3, Users, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";

const stores = [
  { slug: "aura-artisan", name: "Aura Artisan", tag: "Home & Living", color: "#E11D48" },
  { slug: "urban-threads", name: "Urban Threads", tag: "Fashion & Apparel", color: "#2563EB" },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 overflow-x-hidden">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0F17]/80 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-xl tracking-tight">
            <div className="h-8 w-8 rounded-lg bg-indigo-500 grid place-items-center"><Store className="h-5 w-5" /></div>
            Olhub
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => nav("/login")} data-testid="nav-login-btn">Sign in</Button>
            <Button className="bg-indigo-500 hover:bg-indigo-600 rounded-lg" onClick={() => nav("/register")} data-testid="nav-signup-btn">Start selling</Button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-400">Multi-Tenant Commerce OS</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 leading-[1.05]">
            One platform.<br />Infinite storefronts.
          </h1>
          <p className="text-slate-400 text-lg mt-6 max-w-md leading-relaxed">
            Launch subscription-based online stores for vendors, manage the whole marketplace, and give every shop its own branded checkout at <span className="font-mono text-indigo-300">platform.com/store</span>.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 rounded-lg group" onClick={() => nav("/register")} data-testid="hero-start-btn">
              Open your store <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 rounded-lg" onClick={() => nav("/login")} data-testid="hero-login-btn">
              Portal login
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-12 max-w-md">
            {[["3", "Portals"], ["RBAC", "Secure"], ["∞", "Tenants"]].map(([a, b]) => (
              <div key={b}><div className="font-display text-2xl font-bold text-white">{a}</div><div className="text-xs text-slate-500 uppercase tracking-wider">{b}</div></div>
            ))}
          </div>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <img src="https://images.unsplash.com/photo-1605513524006-063ed6ed31e7?auto=format&fit=crop&w=1200&q=80" alt="commerce" className="rounded-2xl border border-slate-800 shadow-2xl object-cover h-[440px] w-full" />
          <div className="absolute -bottom-6 -left-6 backdrop-blur-xl bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 shadow-xl hidden sm:block">
            <div className="text-xs text-slate-400 uppercase tracking-wider">Monthly Recurring Revenue</div>
            <div className="font-display text-2xl font-bold text-emerald-400">₹58,240</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Super Admin", desc: "Approve vendors, manage subscription plans, track MRR & churn, and impersonate stores for support.", to: "/login", color: "text-indigo-400" },
            { icon: Store, title: "Vendor Admin", desc: "Products, variants, inventory alerts, an order pipeline, customer CRM and store branding.", to: "/login", color: "text-blue-400" },
            { icon: ShoppingBag, title: "Customer Store", desc: "Branded storefront, cart, coupons, multi-payment checkout, order tracking and reviews.", to: "/aura-artisan", color: "text-rose-400" },
          ].map((c) => (
            <button key={c.title} onClick={() => nav(c.to)} data-testid={`portal-card-${c.title.toLowerCase().replace(/ /g, "-")}`}
              className="text-left p-8 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-600 hover:bg-slate-900 transition-all duration-200 group">
              <c.icon className={`h-8 w-8 ${c.color}`} />
              <h3 className="font-display text-xl font-semibold mt-5">{c.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{c.desc}</p>
              <span className="inline-flex items-center gap-1 text-sm mt-4 text-slate-300 group-hover:text-white">Explore <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Live demo storefronts</span>
        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          {stores.map((s) => (
            <Link key={s.slug} to={`/${s.slug}`} data-testid={`demo-store-${s.slug}`}
              className="flex items-center justify-between p-6 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-600 transition-all">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl grid place-items-center" style={{ backgroundColor: s.color }}><Store className="h-6 w-6 text-white" /></div>
                <div>
                  <div className="font-display font-semibold text-lg">{s.name}</div>
                  <div className="text-sm text-slate-500">{s.tag} · /{s.slug}</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-500" />
            </Link>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 text-slate-400">
          {[[Package, "Inventory & Variants"], [BarChart3, "Sales Analytics"], [Users, "Customer CRM"], [CreditCard, "UPI · Card · COD"]].map(([Icon, t]) => (
            <div key={t} className="flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-indigo-400" />{t}</div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800/60 py-8 text-center text-slate-600 text-sm">Olhub — Multi-Tenant E-Commerce SaaS</footer>
    </div>
  );
}
